import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ButtonData, MarketingBannerData, TitleInfo } from '../../../common/interfaces';
import { NGXLogger } from 'ngx-logger';
import { ButtonActionType, MarketingBannerType, TextAlignment } from '../../../common/enums';
import { Router } from '@angular/router';
import { CartService } from '../../providers/cart/cart.service';
import { Subscription } from 'rxjs';
import { StateService } from '../../providers/state/state.service';
import { take } from 'rxjs/operators';
import { Asset } from '../../../common/generated-types';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { SwiperContainer } from 'swiper/element';
import Swiper from 'swiper';
import { navigateByNavigationItem, resolveButtonNavigationItem } from '../../../common/utils/navigation-helpers';

@Component({
  selector: 'vsf-dual-text-banner',
  templateUrl: `./dual-text-banner.component.html`,
  styleUrls: ['./dual-text-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DualTextBannerComponent implements OnInit, AfterViewInit {
  @Input() banner!: MarketingBannerData;
  @Input() linePadding = '0';
  @Input() isMobile: boolean = false;
  @Input() titleInfo: TitleInfo | null = null;
  @Input() pageFullWidth: boolean = false;
  @Input() sectionMaxHeight: string = '';

  @ViewChild('slideSwiper') slideSwiper: ElementRef<SwiperContainer>;
  @ViewChild('mobileSlideSwiper') mobileSlideSwiper: ElementRef<SwiperContainer>;

  backgroundMedia: Asset | null;
  bannerMedias: Asset[] = [];
  shadowTransparencyValue: number;
  type: string;
  button: ButtonData | null;
  buttonPosition: string;
  buttonLocationX: number;
  buttonLocationY: number;
  buttonPositionMobile: string;
  buttonLocationXMobile: number;
  buttonLocationYMobile: number;
  buttonAlignment: TextAlignment = TextAlignment.Center;
  autoSlideDelay: number = 3.0;
  mediasHeight = 400;
  mediasWidth = 400;
  inFlight = false;
  baseUrl = '';
  isBrowser: boolean;
  private subscription = new Subscription();
  private swiperInstance: Swiper;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private logger: NGXLogger,
    private cartService: CartService,
    private stateService: StateService,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.stateService.select(state => state.storePath)
    .pipe(take(1)).subscribe(baseUrl => {
        this.baseUrl = baseUrl;
    });
    const marketingBanner = this.banner;
    if(marketingBanner) {
      this.button = marketingBanner.button;
      this.type = marketingBanner.type;
      this.buttonPosition = marketingBanner.buttonPosition;
      this.buttonLocationX = marketingBanner.buttonLocationX;
      this.buttonLocationY = marketingBanner.buttonLocationY;
      this.buttonPositionMobile = marketingBanner.buttonPositionMobile;
      this.buttonLocationXMobile = marketingBanner.buttonLocationXMobile;
      this.buttonLocationYMobile = marketingBanner.buttonLocationYMobile;
      this.buttonAlignment = (this.isMobile ? (marketingBanner.buttonAlignmentMobile ?? marketingBanner.buttonAlignment) : marketingBanner.buttonAlignment) || TextAlignment.Center;
      this.backgroundMedia = this.isMobile ?
        (marketingBanner.backgroundMobileMedia ? marketingBanner.backgroundMobileMedia : marketingBanner.backgroundMedia) :
        marketingBanner.backgroundMedia;
      this.shadowTransparencyValue = marketingBanner.shadowTransparencyValue;
      this.bannerMedias = this.isMobile ?
        (marketingBanner.mobileMedias?.length > 0 ? marketingBanner.mobileMedias : marketingBanner.medias) :
        marketingBanner.medias;
      this.autoSlideDelay = marketingBanner.autoSlideDelay;
    }
  }

  ngAfterViewInit(): void {
    if(!this.isBrowser) {
        return;
    }
    if(this.isMobile) {
        this.swiperInstance = this.mobileSlideSwiper?.nativeElement.swiper;
    } else if(this.isMobile === false) {
        this.swiperInstance = this.slideSwiper?.nativeElement.swiper;
    }
    if(this.swiperInstance && this.autoSlideDelay < 100) {
      this.swiperInstance.params.autoplay = {
          delay: this.autoSlideDelay * 1000,
          disableOnInteraction: false,
      };
      this.swiperInstance.autoplay.start();
    }
    this.changeDetector.detectChanges();
    if(this.type === 'middle-media-sides-text') {
      setTimeout(() => this.calculateSwiperSize());
    }
  }

  px(value?: number): string {
    return (value ?? 0) + 'px';
  }

  percent(value?: number): string {
    return ((value ?? 1) * 100) + '%';
  }

  mediaPercent(value?: number): string {
    if(value && value > 0) {
      const result = 1.0 / (1.0/value -1.0);
      return ((result ?? 1) * 100) + '%';
    }
    return '100%';
  }

  getColStyles(column: 'first' | 'second') {
    const half = 50;
    const align = column === 'first' ? this.banner.textAlignment : this.banner.secColTextAlignment;
    const color = this.banner.textColor;
    return {
      width: half + '%',
      'text-align': align,
      color,
    } as Record<string, string | number>;
  }

  getButtonStyles() {
    const isMob = !!this.isMobile;
    const pos = isMob ? this.buttonPositionMobile : this.buttonPosition;
    if (pos !== 'custom')  {
      return {
        padding: '0.5rem 1rem',
        'background-color': this.button?.backgroundColor,
        color: this.button?.textColor,
        'pointer-events': 'auto',
        'z-index': 10,
      };
    }
    const x = (isMob ? this.buttonLocationXMobile : this.buttonLocationX) ?? 0.5;
    const y = (isMob ? this.buttonLocationYMobile : this.buttonLocationY) ?? 0.5;
    return {
      position: 'absolute',
      left: (x * 100) + '%',
      top: (y * 100) + '%',
      transform: 'translate(-50%, -50%)',
      'background-color': this.button?.backgroundColor,
      color: this.button?.textColor,
      'pointer-events': 'auto',
      'z-index': 10,
    } as Record<string, string | number>;
  }


  buttonClick() {
      this.logger.debug('Button clicked', this.button);
      if(this.button?.enabled && this.button?.actionType) {
          if(this.button.actionType === ButtonActionType.NavigateTo) {
              const navigationItem = resolveButtonNavigationItem(this.button);
              if (navigationItem) {
                  navigateByNavigationItem(this.router, navigationItem, this.baseUrl);
              }
          } else if(this.button.actionType === ButtonActionType.AddVariantToCart) {
              if(this.button.selectedVariantId) {
                  if(this.button.isVariantCustomizable) {
                      this.router.navigate(
                          [`${this.baseUrl}/dp`, this.button.targetProduct?.slug],
                          { queryParams: { variantid: this.button.selectedVariantId } }
                      );
                  } else {
                      this.addToCartAndOpenDrawer(this.button.selectedVariantId);
                  }
              }
          }
      }
  }

  addToCartAndOpenDrawer(productVariantId: string) {
    this.inFlight = true;
    this.changeDetector.markForCheck();

    this.subscription.add(
      this.cartService.addToCartAndOpenDrawer(productVariantId).subscribe({
        next: () => {
          this.inFlight = false;
          this.changeDetector.markForCheck();
        },
        error: err => {
          this.inFlight = false;
          this.logger.error('Error in addToCartAndOpenDrawer', err);
          this.changeDetector.markForCheck();
        },
      }),
    );
  }

  private calculateSwiperSize() {
      if(isPlatformServer(this.platformId)) {
          return;
      }
      const mediaContainer = this.el.nativeElement.querySelector('.media-container');
      if (!mediaContainer) {
          this.logger.warn('Media container not found');
          return { width: 400, height: 400 };
      }
      const rect = mediaContainer.getBoundingClientRect();
      if(rect) {
          if (!this.isMobile) {
              this.mediasHeight = rect.height;
              this.mediasWidth = rect.width;
          }
      }
      this.changeDetector.detectChanges();
  }
}
